import React, { useEffect, useRef, useState } from 'react';
import { Popup, Checkbox } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { Condition, FinanceType } from '@upstart/harmony/lib/constants';
import './CopyBundle.scss';
import CopyBundleModal from './CopyBundleModal';

function useOutsideAlerter(popupRef, closeAll) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      /* Check if click came from Copy Bundle Modal */
      for (const node of event.path) {
        if (node.className && node.className.includes('ReactModal__Overlay')) {
          return;
        }
      }

      /* Close for any click outside this popOver */
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        closeAll();
      }
    }
    /* Bind the event listener */
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popupRef, closeAll]);
}

function CopyBundleButton({ sourceBundle, dealer, updateDealer }) {
  const [isOpen, setOpen] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [copyToPurchaseTypes, setCopyToPurchaseTypes] = useState([]);

  const popupRef = useRef(null);
  useOutsideAlerter(popupRef, () => {
    setOpen(false);
  });

  useEffect(() => {
    setCopyToPurchaseTypes([]);
  }, [isOpen]);

  const purchaseTypes = [
    { condition: Condition.NEW, financeType: FinanceType.LEASE },
    { condition: Condition.NEW, financeType: FinanceType.PURCHASE },
    { condition: Condition.NEW, financeType: FinanceType.CASH },
    { condition: Condition.USED, financeType: FinanceType.PURCHASE },
    { condition: Condition.USED, financeType: FinanceType.CASH },
    { condition: Condition.CPO, financeType: FinanceType.PURCHASE },
    { condition: Condition.CPO, financeType: FinanceType.CASH },
  ];
  // eslint-disable-next-line guard-for-in
  for (const idx in purchaseTypes) {
    const purchaseType = purchaseTypes[idx];
    if (
      sourceBundle.purchaseType.condition === purchaseType.condition &&
      sourceBundle.purchaseType.financeType === purchaseType.financeType
    ) {
      delete purchaseTypes[idx];
    } else {
      purchaseType.id = `${purchaseType.condition}-${purchaseType.financeType}`;
      purchaseType.order = Number(idx);
    }
  }

  const onToggleModal = open => setModalOpen(open);

  const onCopyToChange = (isChecked, purchaseType) => {
    const destinations = isChecked
      ? [...copyToPurchaseTypes, purchaseType]
      : copyToPurchaseTypes.filter(
          copyToPurchaseType => copyToPurchaseType.id !== purchaseType.id,
        );
    destinations.sort((a, b) => a.order - b.order);
    setCopyToPurchaseTypes(destinations);
  };

  const content = (
    <div ref={popupRef} className="popOverContent capitalize">
      <div className="header">
        Copy To:{' '}
        <i onClick={() => setOpen(false)} className="close icon" alt="close" />
      </div>
      {purchaseTypes.map(purchaseType => (
        <div className="purchaseType" key={purchaseType.id}>
          <Checkbox
            checked={copyToPurchaseTypes.some(pT => purchaseType.id === pT.id)}
            onChange={(e, data) => onCopyToChange(data.checked, purchaseType)}
          />
          {`${purchaseType.condition} - ${purchaseType.financeType}`}
        </div>
      ))}
      <div className="actionButtons">
        <button onClick={() => setOpen(false)} className="btn btn-default">
          Cancel
        </button>
        <button
          disabled={!copyToPurchaseTypes.length}
          className="btn btn-primary continue"
          onClick={() => onToggleModal(true)}
        >
          Copy and Save
        </button>
      </div>
      {isModalOpen && (
        <CopyBundleModal
          copyToPurchaseTypes={copyToPurchaseTypes}
          sourceBundle={sourceBundle}
          dealer={dealer}
          updateDealer={updateDealer}
          onClose={() => {
            setModalOpen(false);
          }}
          onCloseAll={() => {
            setOpen(false);
            setModalOpen(false);
          }}
        />
      )}
    </div>
  );

  return (
    <Popup
      open={isOpen}
      content={content}
      className={isModalOpen ? 'modalOpen' : ''}
      on="click"
      position="bottom right"
      size="huge"
      wide="very"
      trigger={
        <button
          className="btn copyBtn"
          onClick={() => {
            setOpen(!isOpen);
          }}
        >
          Copy Bundle
        </button>
      }
    />
  );
}

CopyBundleButton.propTypes = {
  sourceBundle: PropTypes.object,
  updateDealer: PropTypes.func,
  dealer: PropTypes.object,
};

export default CopyBundleButton;
